import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { PollResultView, RankingOption } from "./pollTypes";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { pollColorPalette } from "./utils/pollColorPalette";
import {
  AggregateRank,
  loadRankingResults,
  generateRankColors,
  generateLowRankColors,
  formatRankLabel,
} from "./utils/rankingResultsUtils";
import { loadPollAggregates } from "./utils/loadPollAggregates";
import { RankingHistogram, RankingHistogramV2, RankingScatterPlot, RankingStackedDistribution } from "./visualizations";

interface PollRankingMinimumResultsProps {
  pollId: string;
  options: RankingOption[];
  userOrder: string[];
  hasSubmitted: boolean;
  minimumRequiredRanks: number;
  viewMode?: PollResultView;
}

export const PollRankingMinimumResults = ({
  pollId,
  options,
  userOrder,
  hasSubmitted,
  minimumRequiredRanks,
  viewMode,
}: PollRankingMinimumResultsProps) => {
  const [totals, setTotals] = useState<Record<string, AggregateRank>>({});
  const [totalsFull, setTotalsFull] = useState<Record<string, AggregateRank>>({});
  const [responseCount, setResponseCount] = useState(0);
  const [actualResponseCount, setActualResponseCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<"average" | "user" | "minimum">("user");
  const [scatterAxisMetric, setScatterAxisMetric] = useState<"minimum" | "average">("minimum");

  // Minimum ranking: use minimum required ranks for display buckets
  const displayRankCount = Math.max(1, Math.min(minimumRequiredRanks, options.length));
  const hasOverflow = options.length > displayRankCount;
  const overflowColor = pollColorPalette.lowRankOverflow;
  const resolvedViewMode: PollResultView =
    viewMode === "histogram" || viewMode === "histogram_v2" || viewMode === "scatter_chart"
      ? viewMode
      : "stacked_distribution";
  const minimumRanksForSort = Math.max(0, Math.min(minimumRequiredRanks, displayRankCount));

  useEffect(() => {
    const loadResults = async () => {
      try {
        // Try loading pre-calculated aggregates first
        const aggregates = await loadPollAggregates(pollId, options.length, displayRankCount);
        
        if (aggregates) {
          // Use pre-calculated data
          setTotals(aggregates.totals);
          setTotalsFull(aggregates.totalsFull ?? {});
          setResponseCount(aggregates.responseCount);
          setActualResponseCount(aggregates.responseCount);

          if (viewMode === "histogram_v2" && !aggregates.totalsFull) {
            const data = await loadRankingResults(pollId, options.length, displayRankCount, {
              includeFullCounts: true,
            });
            setTotalsFull(data.totalsFull ?? {});
          }
        } else {
          // Fall back to on-the-fly calculation for backward compatibility
          const data = await loadRankingResults(pollId, options.length, displayRankCount, { includeFullCounts: true });
          setTotals(data.totals);
          setTotalsFull(data.totalsFull ?? {});
          setResponseCount(data.responseCount);
          setActualResponseCount(data.actualResponseCount);
        }
      } catch (error) {
        console.error("Error loading ranking results", error);
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [pollId, options.length, displayRankCount, viewMode]);

  const maxRank = options.length || 1;
  const rankToScore = useCallback(
    (rankValue: number) => Math.max(0, maxRank - rankValue + 1),
    [maxRank]
  );
  const scoreToRank = useCallback(
    (score: number) => maxRank - score + 1,
    [maxRank]
  );
  const getMinimumRankPriority = useCallback(
    (counts: number[]): number[] => {
      const inScopeBuckets = counts.slice(0, minimumRanksForSort);
      if (minimumRanksForSort > 0) {
        const total = inScopeBuckets.reduce((sum, value) => sum + value, 0);
        const priorities = [total];
        let runningTotal = total;
        for (let i = inScopeBuckets.length - 1; i > 0; i--) {
          runningTotal -= inScopeBuckets[i];
          priorities.push(runningTotal);
        }
        return priorities;
      }

      const lowRankBuckets = counts.slice(0, displayRankCount);
      return lowRankBuckets;
    },
    [displayRankCount, minimumRanksForSort]
  );
  const compareByMinimumRank = useCallback(
    (aId?: string, bId?: string) => {
      const aCounts = totals[aId ?? ""]?.counts || Array(displayRankCount + (hasOverflow ? 2 : 0)).fill(0);
      const bCounts = totals[bId ?? ""]?.counts || Array(displayRankCount + (hasOverflow ? 2 : 0)).fill(0);
      const aPriority = getMinimumRankPriority(aCounts);
      const bPriority = getMinimumRankPriority(bCounts);
      const priorityLength = Math.max(aPriority.length, bPriority.length);

      for (let i = 0; i < priorityLength; i++) {
        const aValue = aPriority[i] ?? 0;
        const bValue = bPriority[i] ?? 0;
        if (aValue !== bValue) return bValue - aValue;
      }

      const aRank = totals[aId ?? ""]?.averageRank ?? Number.MAX_SAFE_INTEGER;
      const bRank = totals[bId ?? ""]?.averageRank ?? Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) return aRank - bRank;

      const aKey = aId ?? "";
      const bKey = bId ?? "";
      return aKey.localeCompare(bKey);
    },
    [displayRankCount, getMinimumRankPriority, hasOverflow, totals]
  );

  const userRanks = useMemo(() => {
    const ranks = new Map<string, number>();
    userOrder.forEach((id, index) => ranks.set(id, index + 1));
    return ranks;
  }, [userOrder]);

  const scoreTicks = useMemo(() => {
    const ticks = [rankToScore(maxRank), rankToScore(1)];
    const uniqueTicks = Array.from(new Set(ticks));
    return uniqueTicks.sort((a, b) => a - b);
  }, [maxRank, rankToScore]);
  const averageRankOrder = useMemo(() => {
    const sorted = [...options].sort((a, b) => {
      const aRank = totals[a.id ?? ""]?.averageRank ?? Number.MAX_SAFE_INTEGER;
      const bRank = totals[b.id ?? ""]?.averageRank ?? Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) return aRank - bRank;
      return (a.label ?? "").localeCompare(b.label ?? "");
    });
    const map = new Map<string, number>();
    sorted.forEach((option, idx) => map.set(option.id ?? "", idx + 1));
    return map;
  }, [options, totals]);
  const minimumRankOrder = useMemo(() => {
    const sorted = [...options].sort((a, b) => compareByMinimumRank(a.id, b.id));
    const map = new Map<string, number>();
    sorted.forEach((option, idx) => map.set(option.id ?? "", idx + 1));
    return map;
  }, [compareByMinimumRank, options]);

  const scatterPoints = useMemo(() => {
    const minimumIndex = Math.max(0, Math.min(displayRankCount, minimumRequiredRanks) - 1);
    return options.map((option) => {
      const aggregate = totals[option.id ?? ""];
      const counts = aggregate?.counts || Array(displayRankCount + (hasOverflow ? 2 : 0)).fill(0);
      const averageRank = aggregate?.averageRank ?? maxRank;
      const minimumSupportCount = aggregate?.topKSupport?.[minimumIndex] ?? 0;
      const minimumSupportShare = responseCount > 0 ? (minimumSupportCount / responseCount) * 100 : 0;
      const bestMinimumRankResult = counts.slice(0, displayRankCount).reduce(
        (best, count, idx) => {
          if (count > best.count) return { count, rank: idx + 1 };
          if (count === best.count && count > 0 && idx + 1 < best.rank) return { count, rank: idx + 1 };
          return best;
        },
        { count: 0, rank: displayRankCount + 1 }
      );
      const hasMinimumSupport = bestMinimumRankResult.count > 0;
      const bestMinimumRank = hasMinimumSupport ? bestMinimumRankResult.rank : maxRank + 1;
      const bestMinimumRankLabel = hasMinimumSupport ? formatRankLabel(bestMinimumRank) : "Unranked";

      const userRank = userRanks.get(option.id ?? "");
      const userRankScore = rankToScore(userRank ?? maxRank + 1);
      const averageRankPosition = averageRankOrder.get(option.id ?? "") ?? maxRank;
      const minimumSupportRankPosition = minimumRankOrder.get(option.id ?? "") ?? maxRank;
      const metricRank = scatterAxisMetric === "average" ? averageRankPosition : minimumSupportRankPosition;
      const metricRankScore = rankToScore(metricRank);

      return {
        optionId: option.id,
        label: option.label,
        imageUrl: option.image_url,
        isRankedByUser: userRanks.has(option.id ?? ""),
        averageRank,
        bestMinimumRank,
        bestMinimumRankLabel,
        minimumSupportCount,
        minimumSupportShare,
        averageRankPosition,
        minimumSupportRankPosition,
        averageRankLabel: formatRankLabel(averageRankPosition),
        minimumSupportRankLabel: formatRankLabel(minimumSupportRankPosition),
        userRank,
        userRankLabel: userRank ? formatRankLabel(userRank) : "Unranked",
        xScore: metricRankScore,
        yScore: userRankScore,
      };
    });
  }, [
    options,
    displayRankCount,
    hasOverflow,
    maxRank,
    minimumRequiredRanks,
    averageRankOrder,
    minimumRankOrder,
    rankToScore,
    responseCount,
    totals,
    userRanks,
    scatterAxisMetric,
  ]);

  const sortedOptions = useMemo(() => {
    const optionsWithTopRank = options.filter((option) => {
      const aggregate = totals[option.id ?? ""];
      return (aggregate?.topRankResponses || 0) > 0 || userRanks.has(option.id ?? "");
    });

    const baseSorted = optionsWithTopRank.sort((a, b) => {
      if (sortBy === "user") {
        const aUserRank = userRanks.get(a.id ?? "");
        const bUserRank = userRanks.get(b.id ?? "");

        if (aUserRank && bUserRank) return aUserRank - bUserRank;
        if (aUserRank) return -1;
        if (bUserRank) return 1;
      }

      if (sortBy === "minimum") {
        return compareByMinimumRank(a.id, b.id);
      }

      const aRank = totals[a.id ?? ""]?.averageRank ?? Number.MAX_SAFE_INTEGER;
      const bRank = totals[b.id ?? ""]?.averageRank ?? Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) return aRank - bRank;
      return (a.label ?? "").localeCompare(b.label ?? "");
    });

    return baseSorted;
  }, [compareByMinimumRank, options, totals, sortBy, userRanks]);

  // Include overflow bucket for unranked items
  const rankColors = useMemo(
    () => generateRankColors(displayRankCount, hasOverflow ? "dual" : "none"),
    [displayRankCount, hasOverflow]
  );
  const rankColorsFull = useMemo(() => {
    const lowRankCount = Math.max(0, maxRank - displayRankCount);
    const topColors = generateRankColors(displayRankCount, "none");
    if (lowRankCount === 0) {
      return topColors;
    }
    return [...topColors, ...generateLowRankColors(lowRankCount)];
  }, [displayRankCount, maxRank]);

  const histogramMaxDensity = useMemo(() => {
    const overflowSpan = Math.max(0, maxRank - displayRankCount);
    const overflowBucketSpan = hasOverflow ? Math.max(1, overflowSpan) : 0;

    return options.reduce((currentMax, option) => {
      const aggregate = totals[option.id ?? ""];
      const counts =
        aggregate?.counts || Array(displayRankCount + (hasOverflow ? 2 : 0)).fill(0);

      const bucketDensities = counts.slice(0, displayRankCount).map((count) => count);
      const overflowTotal = hasOverflow
        ? (counts[displayRankCount] ?? 0) + (counts[displayRankCount + 1] ?? 0)
        : 0;
      const overflowDensity = overflowBucketSpan > 0 ? overflowTotal / overflowBucketSpan : 0;

      const optionMax = Math.max(...bucketDensities, overflowDensity, 0);
      return Math.max(currentMax, optionMax);
    }, 0);
  }, [options, totals, displayRankCount, hasOverflow, maxRank]);

  const histogramV2MaxDensity = useMemo(() => {
    if (!hasOverflow) return 0;
    const unrankedIndex = maxRank;

    return options.reduce((currentMax, option) => {
      const aggregate = totalsFull[option.id ?? ""];
      if (!aggregate) return currentMax;
      const counts = aggregate.counts || Array(maxRank + 1).fill(0);
      const topBuckets = counts.slice(0, displayRankCount);
      const lowBuckets = counts.slice(displayRankCount, maxRank);
      const unrankedCount = counts[unrankedIndex] ?? 0;
      const unrankedDensity = unrankedCount;

      const optionMax = Math.max(...topBuckets, ...lowBuckets, unrankedDensity, 0);
      return Math.max(currentMax, optionMax);
    }, 0);
  }, [displayRankCount, hasOverflow, maxRank, options, totalsFull]);

  if (loading)
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size={120} />
      </div>
    );

  if (responseCount === 0) {
    if (actualResponseCount > 0) {
      return (
        <div className="text-center py-12">
          <p className="text-lg font-medium text-destructive">Data Error: Allocations not found</p>
          <p className="text-sm text-muted-foreground mt-2">
            {actualResponseCount} response(s) recorded but allocation data is missing.
            This may have been caused by poll configuration changes.
          </p>
        </div>
      );
    }
    return (
      <div className="text-center py-12">
        <p className="text-lg font-medium">No responses yet</p>
        <p className="text-sm text-muted-foreground mt-2">Be the first to share your ranking!</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Card className="p-4 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold">Results</h3>
            <p className="text-sm text-muted-foreground">
              Based on {responseCount} response{responseCount === 1 ? "" : "s"}
            </p>
            {!hasSubmitted && (
              <p className="text-xs text-muted-foreground mt-1">
                You are seeing public results. Submit a ranking to compare against your own order.
              </p>
            )}
          </div>

        {resolvedViewMode === "scatter_chart" ? (
          <div className="flex items-center gap-2 text-sm">
            <label htmlFor="scatter-metric" className="text-muted-foreground whitespace-nowrap">
              X-axis metric
            </label>
            <select
              id="scatter-metric"
              value={scatterAxisMetric}
              onChange={(e) => setScatterAxisMetric(e.target.value as "minimum" | "average")}
              className="h-9 rounded-md border bg-background px-3 text-sm"
            >
              <option value="minimum">Rank by minimum support</option>
              <option value="average">Rank by average</option>
            </select>
          </div>
        ) : (
            <div className="flex items-center gap-2 text-sm">
              <label htmlFor="rank-sort" className="text-muted-foreground whitespace-nowrap">
                Sort by
              </label>
              <select
                id="rank-sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as "average" | "user" | "minimum")}
                className="h-9 rounded-md border bg-background px-3 text-sm"
              >
                <option value="average">Response average</option>
                <option value="minimum">Most min ranked</option>
                <option value="user">Your ranking</option>
              </select>
            </div>
          )}
        </div>

        {resolvedViewMode === "scatter_chart" ? (
          <>
            <RankingScatterPlot
              data={scatterPoints}
              xTicks={scoreTicks}
              xLabel={scatterAxisMetric === "average" ? "Rank by average" : "Rank by minimum support"}
              yLabel="Your rank"
              scoreToRank={scoreToRank}
              maxRankScore={rankToScore(1)}
            />
            <div className="space-y-2 text-xs text-muted-foreground">
              <p>
                Points compare your ranking on the vertical axis to the crowd&rsquo;s{" "}
                {scatterAxisMetric === "average"
                  ? "rank based on average placement on the horizontal axis"
                  : "rank based on minimum-rank support on the horizontal axis"}.
                Dots on the diagonal match the community&rsquo;s order.
              </p>
              <div className="flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-6 rounded-sm border border-muted-foreground/60" />
                  <span>Your ranked choices</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-6 rounded-sm border border-muted-foreground/60 bg-muted" />
                  <span className="text-muted-foreground">Unranked (greyed)</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2">
              {sortedOptions.map((option) => {
                const aggregate = totals[option.id ?? ""];
                const aggregateFull = totalsFull[option.id ?? ""];
                const averageRank = aggregate?.averageRank ?? 0;
                const counts = aggregate?.counts || Array(displayRankCount + (hasOverflow ? 2 : 0)).fill(0);
                const countsFull = aggregateFull?.counts || Array(maxRank + 1).fill(0);
                const userRank = userRanks.get(option.id ?? "");
                const userRankBucketIndex = userRank
                  ? Math.min(userRank, displayRankCount + (hasOverflow ? 1 : 0)) - 1
                  : null;
                const userRankBucketIndexFull = userRank ? Math.min(userRank, maxRank) - 1 : null;
                const optionResponseTotal =
                  Math.max(aggregate?.totalResponses || 0, counts.reduce((sum, value) => sum + value, 0), 1);
                const baseTotal = optionResponseTotal;
                const averagePosition = ((averageRank - 1) / Math.max(1, maxRank - 1)) * 100;
                const histogramAveragePosition = ((averageRank - 1) / Math.max(1, maxRank - 1)) * 100;
                const bucketLabels = counts.map((_, rankIndex) => {
                  if (rankIndex < displayRankCount) return formatRankLabel(rankIndex + 1);
                  return rankIndex === displayRankCount ? `Rank ${displayRankCount + 1}+ (explicit)` : "Unranked";
                });
                const histogramResponseTotal = optionResponseTotal;
                const averageLabel = averageRank ? averageRank.toFixed(1) : "—";

                return (
                  <div
                    key={option.id}
                    className="grid grid-cols-[5rem,1fr] gap-x-4 gap-y-2 items-center p-3 rounded-lg transition"
                  >
                    <div className="col-span-2 flex items-start justify-between gap-2 text-left leading-tight">
                      <div className="flex items-center gap-2 text-base font-semibold min-w-0">
                        <span className="truncate">{option.label}</span>
                        {option.description && (
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground hover:text-foreground border"
                                aria-label={`About ${option.label}`}
                              >
                                <Info className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" align="center" className="max-w-xs text-sm">
                              {option.description}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground whitespace-nowrap">
                        <span className="font-semibold text-foreground">{averageLabel}</span>
                        <span>avg</span>
                      </div>
                    </div>

                    <div className="flex w-20">
                      <div className="w-14 h-14 rounded-md overflow-hidden flex items-center justify-center text-sm font-semibold text-muted-foreground border">
                        {option.image_url ? (
                          <img src={option.image_url} alt={option.label} className="w-full h-full object-cover" loading="lazy" width={56} height={56} />
                        ) : (
                          <span>{option.label?.charAt(0).toUpperCase() ?? "?"}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 space-y-3">
                      {resolvedViewMode === "histogram" ? (
                        <RankingHistogram
                          optionId={option.id}
                          counts={counts}
                          bucketLabels={bucketLabels}
                          rankColors={rankColors}
                          overflowColor={overflowColor}
                          userRank={userRank}
                          userRankBucketIndex={userRankBucketIndex}
                          averageRank={averageRank}
                          averagePosition={histogramAveragePosition}
                          displayRankCount={displayRankCount}
                          maxRank={maxRank}
                          responseCount={histogramResponseTotal}
                          maxDensity={histogramMaxDensity}
                          showAverageMarker={false}
                        />
                      ) : resolvedViewMode === "histogram_v2" ? (
                        <RankingHistogramV2
                          optionId={option.id}
                          counts={countsFull}
                          rankColors={rankColorsFull}
                          userRank={userRank}
                          userRankBucketIndex={userRankBucketIndexFull}
                          averageRank={averageRank}
                          averagePosition={histogramAveragePosition}
                          displayRankCount={displayRankCount}
                          maxRank={maxRank}
                          responseCount={histogramResponseTotal}
                          maxDensity={histogramV2MaxDensity}
                          showAverageMarker={false}
                        />
                      ) : (
                        <RankingStackedDistribution
                          optionId={option.id}
                          counts={counts}
                          bucketLabels={bucketLabels}
                          rankColors={rankColors}
                          overflowColor={overflowColor}
                          userRank={userRank}
                          userRankBucketIndex={userRankBucketIndex}
                          baseTotal={baseTotal}
                          averageRank={averageRank}
                          averagePosition={averagePosition}
                          showAverageMarker={false}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="space-y-2 text-xs text-muted-foreground">
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span>Required ranking range</span>
                  <span>{formatRankLabel(1)}</span>
                  <div className="flex overflow-hidden rounded">
                    {(resolvedViewMode === "histogram_v2"
                      ? rankColorsFull.slice(0, displayRankCount)
                      : rankColors.slice(0, displayRankCount)
                    ).map((color, idx) => (
                      <div
                        key={`legend-rank-${idx}`}
                        className="h-3 w-3"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <span>{formatRankLabel(displayRankCount)}</span>
                </div>
              </div>

              {hasOverflow && (
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    {resolvedViewMode === "histogram_v2" ? (
                      <>
                        <span>Optional ranking range</span>
                        <span>{formatRankLabel(displayRankCount + 1)}</span>
                        <div className="flex overflow-hidden rounded">
                          {rankColorsFull.slice(displayRankCount, maxRank).map((color, idx) => (
                            <div
                              key={`legend-low-rank-${idx}`}
                              className="h-3 w-3"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        <span>{formatRankLabel(maxRank)}</span>
                      </>
                    ) : (
                      <>
                        <div
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: rankColors[displayRankCount] }}
                        />
                        <span>Not required ranking range</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                {hasOverflow && (
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{
                        backgroundColor:
                          resolvedViewMode === "histogram_v2"
                            ? pollColorPalette.unrankedOverflow
                            : rankColors[displayRankCount + 1],
                      }}
                    />
                    <span>Unranked (imputed)</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <div className="w-6 h-4 rounded border-2 border-black" />
                  <span>Your submission</span>
                </div>
              </div>

              <div className="space-y-1 leading-relaxed">
                <p>
                  Average ranks are shown as the number beside each option and count unranked submissions as the lowest possible rank to keep scores comparable.
                </p>
                <p>
                  &ldquo;Most min ranked&rdquo; sorts by how many responses placed an option within the required ranks; ties favor those with more support in the next rank buckets and then by overall average.
                </p>
              </div>
            </div>
          </>
        )}
      </Card>
    </TooltipProvider>
  );
};
